<script lang="ts" setup>
import { ref, computed, watch, onUnmounted } from "vue";
import type {
  IServiceModel,
  HttpServiceModel,
  HttpServiceModelPaging,
  WebhookServiceModel,
  JavaScriptServiceModel,
  ExpressionServiceModel,
  SftpServiceModel,
  NoneServiceModel,
  ServiceModelOption
} from "@/types/workflow-types";

import {  serviceModelTypes} from "@/types/workflow-types";

const props = defineProps<{
  isVisible: boolean;
  parentNodeId: string | null;
}>();

const emit = defineEmits<{
  close: [];
  addService: [serviceModelType: string, parentNodeId: string | null];
}>();

const searchQuery = ref("");
const panelRef = ref<HTMLElement>();




// Filtered service models based on search
const filteredServiceModels = computed(() => {
  if (!searchQuery.value) return serviceModelTypes;

  const query = searchQuery.value.toLowerCase();
  return serviceModelTypes.filter(
    (service) =>
      service.name.toLowerCase().includes(query) ||
      service.description.toLowerCase().includes(query) ||
      service.category.toLowerCase().includes(query)
  );
});

// Group by category
const groupedServiceModels = computed(() => {
  const groups = new Map<string, ServiceModelOption[]>();

  filteredServiceModels.value.forEach((service) => {
    if (!groups.has(service.category)) {
      groups.set(service.category, []);
    }
    groups.get(service.category)!.push(service);
  });

  return Array.from(groups.entries()).map(([category, services]) => ({
    category,
    services,
  }));
});

function handleServiceSelect(serviceModel: ServiceModelOption) {
  emit("addService", serviceModel.type, props.parentNodeId);
}

function handleClose() {
  emit("close");
}

// Click outside detection
function handleClickOutside(event: MouseEvent) {
  if (
    props.isVisible &&
    panelRef.value &&
    !panelRef.value.contains(event.target as Node)
  ) {
    handleClose();
  }
}

// Watch for visibility changes
watch(
  () => props.isVisible,
  async (isVisible) => {
    if (isVisible) {
      // Add listener with delay to avoid immediate closure
      setTimeout(() => {
        if (props.isVisible) {
          document.addEventListener("click", handleClickOutside);
        }
      }, 100);
    } else {
      document.removeEventListener("click", handleClickOutside);
      // Clear search when panel closes
      searchQuery.value = "";
    }
  }
);

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div ref="panelRef" class="add-service-panel" :class="{ visible: isVisible }">
    <div class="panel-header d-flex justify-content-between align-items-center">
      <h2 class="h3 mb-0">Add Service</h2>
      <button type="button" class="btn-close" @click="handleClose"></button>
    </div>

    <div class="panel-content">
      <!-- Search Bar -->
      <div class="mb-4">
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input
            type="text"
            class="form-control"
            placeholder="Search service types..."
            v-model="searchQuery"
          />
          <button
            v-if="searchQuery"
            class="bb-btn-secondary-cta btn-sm"
            type="button"
            @click="searchQuery = ''"
          >
            <i class="bi bi-x"></i>
          </button>
        </div>
      </div>

      <!-- Service Categories -->
      <div v-if="groupedServiceModels.length > 0">
        <div
          v-for="group in groupedServiceModels"
          :key="group.category"
          class="service-category mb-4"
        >
          <div class="d-flex align-items-center gap-2 mb-3">
            <h3 class="category-title text-muted mb-0">
              {{ group.category }}
            </h3>
            <span class="badge bg-secondary small">{{
              group.services.length
            }}</span>
          </div>

          <div class="service-list">
            <div
              v-for="service in group.services"
              :key="service.id"
              class="service-item d-flex align-items-start gap-3 p-3 rounded border"
              @click="handleServiceSelect(service)"
            >
              <div
                class="service-icon d-flex align-items-center justify-content-center"
              >
                <i :class="service.icon"></i>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <div class="service-name">
                    {{ service.name }}
                  </div>
                  <span class="badge bg-secondary small">{{
                    service.type
                  }}</span>
                </div>
                <div class="service-description text-muted small">
                  {{ service.description }}
                </div>
              </div>
              <div class="add-icon">
                <i class="bi bi-plus-circle text-primary"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="d-flex flex-column align-items-center justify-content-center py-5"
      >
        <i class="bi bi-search text-muted fs-1 mb-3"></i>
        <h4 class="text-muted">No service types found</h4>
        <p class="text-muted small mb-3">
          No service types match "{{ searchQuery }}"
        </p>
        <button
          class="btn btn-outline-primary btn-sm"
          @click="searchQuery = ''"
        >
          <i class="bi bi-x-circle me-1"></i>
          Clear search
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.add-service-panel {
  position: absolute;
  top: 0;
  right: -400px;
  width: 400px;
  height: 100%;
  background: var(--bs-body-bg);
  border-left: 1px solid var(--bs-border-color);
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.1);
  transition: right 0.3s ease-in-out;
  z-index: 1000;
  overflow-y: auto;
}

.add-service-panel.visible {
  right: 0;
}

.panel-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
  background: var(--bs-light);
}

.panel-content {
  padding: 1.5rem;
}

.category-title {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.add-service-panel .service-item {
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  border-color: var(--bs-border-color);
}

.add-service-panel .service-item:hover {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary-bg-subtle);
  transform: translateY(-1px);
}

/* Neutral icon chip — matches the canvas node (ServiceNode) so the palette previews what lands on
   the graph: subtle grey surface, muted icon, subtle border; larger here for the roomier card. */
.service-icon {
  width: 40px;
  height: 40px;
  background: var(--bs-light-bg-subtle);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  font-size: 20px;
  color: var(--bs-secondary-color);
  flex-shrink: 0;
}

[data-bs-theme="dark"] .service-icon {
  background: var(--bs-secondary-bg);
  border-color: var(--bs-secondary-border-subtle);
}

.service-name {
  font-size: 0.95rem;
}

.service-description {
  line-height: 1.4;
}

.add-icon {
  opacity: 0.6;
  transition: opacity 0.15s ease-in-out;
  font-size: 1.25rem;
}

.service-item:hover .add-icon {
  opacity: 1;
}

/* Dark mode support */
[data-bs-theme="dark"] .panel-header {
  background: #374151;
}

[data-bs-theme="dark"] .service-item:hover {
  background-color: #374151;
}
</style>
